import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Phone, Mail, User, Heart, Menu, X, LogOut } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { supabase } from "@/integrations/supabase/client";

const getStoredAuthUser = () => {
  try {
    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    const raw = localStorage.getItem(`sb-${projectId}-auth-token`);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const session = parsed?.currentSession ?? parsed?.session ?? (Array.isArray(parsed) ? parsed[0] : null);
    return session?.user ?? null;
  } catch {
    return null;
  }
};

export const Navbar = () => {
  const [user, setUser] = useState<any>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    const authStorageKey = `sb-${projectId}-auth-token`;

    setUser(getStoredAuthUser());

    const onStorageChange = (event: StorageEvent) => {
      if (!event.key || event.key === authStorageKey) {
        setUser(getStoredAuthUser());
      }
    };

    window.addEventListener("storage", onStorageChange);
    return () => window.removeEventListener("storage", onStorageChange);
  }, []);

  return (
    <nav className="bg-background border-b sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center space-x-2">
            <img src="/lovable-uploads/abe5bbca-52aa-4071-ba12-16af51cf5fac.webp" alt="MN monogram real estate logo" className="h-8 w-8 object-contain" loading="eager" decoding="async" width={32} height={32} />
            <span className="text-xl font-bold">MNRE</span>
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            <Link to="/properties" className="text-foreground hover:text-primary transition-colors">
              Properties
            </Link>
            <Link to="/about" className="text-foreground hover:text-primary transition-colors">
              About
            </Link>
            <Link to="/nri-investors" className="text-foreground hover:text-primary transition-colors">
              NRI Investors
            </Link>
            <Link to="/blog" className="text-foreground hover:text-primary transition-colors">
              Blog
            </Link>
            <Link to="/forms" className="text-foreground hover:text-primary transition-colors">
              Forms
            </Link>
            <Link to="/contact" className="text-foreground hover:text-primary transition-colors">
              Contact
            </Link>
          </div>

          <div className="flex items-center space-x-4">
            <div className="hidden lg:flex items-center space-x-4 text-sm text-muted-foreground">
              <div className="flex items-center">
                <Phone className="h-4 w-4 mr-1" />
                <span>+91 7021011304</span>
              </div>
              <div className="flex items-center">
                <Mail className="h-4 w-4 mr-1" />
                <span>hello@mnre.in</span>
              </div>
            </div>
            
            {user ? (
              <div className="hidden md:flex items-center space-x-2">
                <Link to="/dashboard">
                  <Button variant="ghost" size="sm" className="flex items-center">
                    <Heart className="h-4 w-4 mr-1" />
                    My Favorites
                  </Button>
                </Link>
                <Link to="/dashboard">
                  <Button variant="outline" size="sm" className="flex items-center">
                    <User className="h-4 w-4 mr-1" />
                    Dashboard
                  </Button>
                </Link>
                <Button variant="ghost" size="sm" className="flex items-center text-muted-foreground" onClick={() => supabase.auth.signOut()}>
                  <LogOut className="h-4 w-4 mr-1" />
                  Sign Out
                </Button>
              </div>
            ) : (
              <Link to="/auth" className="hidden md:inline-flex">
                <Button variant="outline" size="sm">
                  Sign In
                </Button>
              </Link>
            )}

            {/* Mobile hamburger */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t bg-background">
          <div className="container mx-auto px-4 py-4 flex flex-col space-y-4">
            <Link to="/properties" className="text-foreground hover:text-primary transition-colors py-2" onClick={() => setMobileOpen(false)}>
              Properties
            </Link>
            <Link to="/about" className="text-foreground hover:text-primary transition-colors py-2" onClick={() => setMobileOpen(false)}>
              About
            </Link>
            <Link to="/nri-investors" className="text-foreground hover:text-primary transition-colors py-2" onClick={() => setMobileOpen(false)}>
              NRI Investors
            </Link>
            <Link to="/blog" className="text-foreground hover:text-primary transition-colors py-2" onClick={() => setMobileOpen(false)}>
              Blog
            </Link>
            <Link to="/forms" className="text-foreground hover:text-primary transition-colors py-2" onClick={() => setMobileOpen(false)}>
              Forms
            </Link>
            <Link to="/contact" className="text-foreground hover:text-primary transition-colors py-2" onClick={() => setMobileOpen(false)}>
              Contact
            </Link>

            <div className="flex flex-col space-y-2 text-sm text-muted-foreground pt-2 border-t">
              <div className="flex items-center">
                <Phone className="h-4 w-4 mr-2" />
                <span>+91 7021011304</span>
              </div>
              <div className="flex items-center">
                <Mail className="h-4 w-4 mr-2" />
                <span>hello@mnre.in</span>
              </div>
            </div>

            <div className="pt-2 border-t">
              {user ? (
                <div className="flex flex-col space-y-2">
                  <Link to="/dashboard" onClick={() => setMobileOpen(false)}>
                    <Button variant="ghost" size="sm" className="w-full justify-start">
                      <Heart className="h-4 w-4 mr-2" />
                      My Favorites
                    </Button>
                  </Link>
                  <Link to="/dashboard" onClick={() => setMobileOpen(false)}>
                    <Button variant="outline" size="sm" className="w-full justify-start">
                      <User className="h-4 w-4 mr-2" />
                      Dashboard
                    </Button>
                  </Link>
                  <Button variant="ghost" size="sm" className="w-full justify-start text-muted-foreground" onClick={() => { supabase.auth.signOut(); setMobileOpen(false); }}>
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </Button>
                </div>
              ) : (
                <Link to="/auth" onClick={() => setMobileOpen(false)}>
                  <Button variant="outline" size="sm" className="w-full">
                    Sign In
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
